// src/pages/PersonelListesi.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection, getDocs, query, where, addDoc, doc, updateDoc, serverTimestamp,
  getDoc, setDoc,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import * as XLSX from "xlsx";

/* -------------------- Helpers -------------------- */
const byLocale = (a, b) => a.localeCompare(b, "tr", { sensitivity: "base" });
const formatDateDot = (dateStr = "") => {
  if (!dateStr) return "";
  const parts = dateStr.replaceAll("/", ".").replaceAll("-", ".").split(".");
  if (parts.length === 3) {
    let [a, b, c] = parts.map((x) => x.trim());
    if (a.length === 4) return `${c.padStart(2, "0")}.${b.padStart(2, "0")}.${a}`;
    if (c.length === 4) return `${a.padStart(2, "0")}.${b.padStart(2, "0")}.${c}`;
  }
  return dateStr.replaceAll("-", ".");
};
const fromDateInputToDot = (val = "") => {
  if (!val) return "";
  const [y, m, d] = val.split("-");
  return `${d.padStart(2, "0")}-${m.padStart(2, "0")}-${y}`;
};
const toISO = (dt = "") => {
  if (!dt) return "";
  const norm = dt.replaceAll("-", ".");
  const [d, m, y] = norm.split(".");
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};
const isActiveByTarih = (tarih = {}) => {
  const gi = Object.keys(tarih).filter((k) => k.startsWith("giris")).length;
  const ci = Object.keys(tarih).filter((k) => k.startsWith("cikis")).length;
  return gi > ci;
};
const lastEntry = (tarih = {}, prefix = "giris") => {
  const arr = Object.entries(tarih).filter(([k]) => k.startsWith(prefix)).sort(([a],[b])=>a.localeCompare(b));
  if (arr.length === 0) return null;
  return { key: arr[arr.length - 1][0], value: formatDateDot(arr[arr.length - 1][1]) };
};
const lastExit = (tarih = {}) => lastEntry(tarih, "cikis");
const upperTR = (s = "") => s.toLocaleUpperCase("tr-TR");
const lowerTR = (s = "") => s.toLocaleLowerCase("tr-TR");
const nameCaseTR = (s = "") => lowerTR(s).split(/\s+/).filter(Boolean).map(w=>w[0].toLocaleUpperCase("tr-TR")+w.slice(1)).join(" ");
const digitsOnly = (s = "") => (s || "").replace(/\D+/g, "");
const normalizeIBAN = (s = "") => upperTR((s || "").replace(/\s+/g, ""));
const isTRIbanFormatLikely = (iban = "") => {
  const v = normalizeIBAN(iban);
  if (!v.startsWith("TR")) return false;
  return v.length >= 24 && v.length <= 26 && /^[A-Z0-9]+$/.test(v);
};
const bankCodeFromTRIban = (iban = "") => {
  const v = normalizeIBAN(iban);
  if (!v.startsWith("TR") || v.length < 8) return "";
  return v.slice(4, 8);
};
const isAkbankIban = (iban = "") => bankCodeFromTRIban(iban) === "0046";
const DESIGN_MODULE = "personel_listesi";
const TABLE_COLUMNS = [
  { key: "ad", label: "AD", className: "col-ad" },
  { key: "soyad", label: "SOYAD", className: "col-soyad" },
  { key: "tc", label: "TC", className: "col-tc" },
  { key: "gorev", label: "GOREV", className: "col-gorev" },
  { key: "tesis", label: "TESIS", className: "col-tesis" },
  { key: "tecrube", label: "TECRUBE (ay)", className: "col-tecrube" },
  { key: "telefon", label: "TELEFON", className: "col-telefon", defaultVisible: false },
  { key: "adres", label: "ADRES", className: "col-adres", defaultVisible: false },
  { key: "yakinAdSoyad", label: "YAKINI", className: "col-yakin", defaultVisible: false },
  { key: "yakinTelefon", label: "YAKIN TEL.", className: "col-yakin-tel", defaultVisible: false },
  { key: "yakinIliski", label: "YAKINLIK", className: "col-yakinlik", defaultVisible: false },
  { key: "iban", label: "IBAN", className: "col-iban", defaultVisible: false },
  { key: "maas", label: "MAAS", className: "col-maas", defaultVisible: false },
  { key: "sgkDurumu", label: "SGK", className: "col-sgk", defaultVisible: false },
  { key: "dogumTarihi", label: "DOGUM TARIHI", className: "col-dogum", defaultVisible: false },
  { key: "ogrenimDurumu", label: "OGRENIM", className: "col-ogrenim", defaultVisible: false },
  { key: "giris", label: "GIRIS TARIHLERI", className: "col-giris" },
  { key: "cikis", label: "CIKIS TARIHLERI", className: "col-cikis" },
  { key: "durum", label: "DURUM", className: "col-durum" },
];

async function userCanVoteBlacklist(email) {
  if (!email) return false;
  const snap = await getDocs(query(collection(db, "users"), where("email","==",email), where("canBlacklist","==",true)));
  return !snap.empty;
}
async function fetchVotesMap() {
  const snap = await getDocs(collection(db, "blacklistVotes"));
  const map = new Map();
  snap.forEach((d) => {
    const v = d.data();
    if (!v?.personelId || !v?.createdBy) return;
    const key = v.personelId;
    const email = String(v.createdBy).toLowerCase();
    if (!map.has(key)) map.set(key, new Set());
    map.get(key).add(email);
  });
  return map;
}

const PersonelListesi = () => {
  const [formData, setFormData] = useState({
    ad:"", soyad:"", tc:"", gorev:"", not:"", tesis:"", tecrube:"",
    girisTarihi:"", cikisTarihi:"",
    telefon:"", adres:"", yakinAdSoyad:"", yakinTelefon:"", yakinIliski:"",
    iban:"",
    maas:"",
    sgkDurumu:"",           // yeni
    dogumTarihi:"",         // yeni
    ogrenimDurumu:""        // yeni
  });
  const [formMode, setFormMode] = useState("create");
  const [editTarget, setEditTarget] = useState(null);
  const [formSnapshot, setFormSnapshot] = useState(null);
  const [extraOpen, setExtraOpen] = useState(false);

  const [personeller, setPersoneller] = useState([]);
  const [filters, setFilters] = useState({
    ad:"", soyad:"", tc:"", gorev:"", tecrube:"",
    tesisList:[],
    girisFrom:"", girisTo:"",
    cikisFrom:"", cikisTo:"",
    durum:"Aktif",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [columnLayout, setColumnLayout] = useState(TABLE_COLUMNS.map((c) => ({
    key: c.key,
    visible: c.defaultVisible !== false,
  })));

  const [editedData, setEditedData] = useState({});
  const isDirtyBulk = Object.keys(editedData).length > 0;

  const me = auth?.currentUser?.email?.toLowerCase() || "";
  const [votesMap, setVotesMap] = useState(new Map());
  const [canVote, setCanVote] = useState(false);
  const voteCountFor = (id) => votesMap.get(id)?.size || 0;
  const isThreeOfThree = (p) => p.karaListe === true || voteCountFor(p.id) >= 3;

  /* ---------- ROL & TESİS YETKİSİ (Navbar mantığıyla) ---------- */
  const [isAdminModule, setIsAdminModule] = useState(false);   // users.personeltakip.yonetici === true
  const [isHR, setIsHR] = useState(false);                     // giris && takip
  const [isSiteManager, setIsSiteManager] = useState(false);   // !giris && takip
  const [isForeman, setIsForeman] = useState(false);           // giris && !takip

  // ✅ Formen tablo & formu görmez; sadece özet görür
  const canViewList = isAdminModule || isHR || isSiteManager;
  const canUseForm  = canViewList;

  const [allowedTesis, setAllowedTesis] = useState([]);

  useEffect(() => {
    (async () => {
      // Kullanıcı rolünü users.personeltakip’ten çöz
      try {
        const uSnap = await getDocs(query(collection(db, "users"), where("email", "==", me)));
        let pt = {};
        uSnap.forEach((d) => { pt = (d.data() || {}).personeltakip || {}; });
        const giris = pt.giris === true;
        const takip = pt.takip === true;
        const yonetici = pt.yonetici === true;
        setIsAdminModule(!!yonetici);
        setIsHR(!!(giris && takip));           // İK
        setIsSiteManager(!!(!giris && takip)); // Saha Yöneticisi
        setIsForeman(!!(giris && !takip));     // Formen
      } catch {
        setIsAdminModule(false); setIsHR(false); setIsSiteManager(false); setIsForeman(false);
      }

      // Kullanıcının görebileceği tesisler
      try {
        const pSnap = await getDocs(query(collection(db, "personeller"), where("email","==", me)));
        const own = new Set();
        pSnap.forEach((d) => {
          const v = d.data() || {};
          if (v.tesis) own.add(String(v.tesis).toUpperCase());
          let extra = v.gorebilecegiTesisler;
          if (typeof extra === "string") extra = extra.split(",").map(x=>x.trim()).filter(Boolean);
          (extra || []).forEach((t)=> own.add(String(t).toUpperCase()));
        });
        setAllowedTesis(Array.from(own));
      } catch {
        setAllowedTesis([]);
      }
    })();
  }, [me]);

  useEffect(() => {
    const loadDesign = async () => {
      if (!me) return;
      try {
        const designId = `${DESIGN_MODULE}_${encodeURIComponent(me)}`;
        const snap = await getDoc(doc(db, "reportDesigns", designId));
        const saved = snap.exists() ? snap.data()?.columns : null;
        if (!Array.isArray(saved)) return;
        const validKeys = new Set(TABLE_COLUMNS.map((c) => c.key));
        const clean = saved
          .map((c) => (typeof c === "string" ? { key: c, visible: true } : { key: c?.key, visible: c?.visible !== false }))
          .filter((c) => c.key && validKeys.has(c.key));
        const missing = TABLE_COLUMNS
          .filter((c) => !clean.some((s) => s.key === c.key))
          .map((c) => ({ key: c.key, visible: c.defaultVisible !== false }));
        setColumnLayout([...clean, ...missing]);
      } catch (err) {
        console.error("Personel listesi tasarımı yüklenemedi:", err);
      }
    };
    loadDesign();
  }, [me]);

  useEffect(() => {
    (async () => {
      await fetchPersoneller();
      setVotesMap(await fetchVotesMap());
      setCanVote(await userCanVoteBlacklist(me));
    })();
    // eslint-disable-next-line
  }, [allowedTesis.length]);

  const fetchPersoneller = async () => {
    const snap = await getDocs(collection(db, "personeller"));
    let data = snap.docs.map((docu) => ({ id: docu.id, ...docu.data() }));
    // Tesis yetkisine göre kısıtla (eğer liste boş ise ‘hiçbiri’ anlamına gelir)
    if (allowedTesis.length > 0) {
      const allow = new Set(allowedTesis.map(t=>String(t).toUpperCase()));
      data = data.filter(p => allow.has(String(p.tesis || "").toUpperCase()));
    } else {
      data = []; // hiç yetki yoksa listede görünmesin
    }
    data.sort((a,b)=>
      (a.ad||"").localeCompare(b.ad||"","tr",{sensitivity:"base"}) ||
      (a.soyad||"").localeCompare(b.soyad||"","tr",{sensitivity:"base"}) ||
      (a.tesis||"").localeCompare(b.tesis||"","tr",{sensitivity:"base"}) ||
      (a.gorev||"").localeCompare(b.gorev||"","tr",{sensitivity:"base"})
    );
    setPersoneller(data);
  };

  const diffObjects = (before = {}, after = {}, keys = []) => {
    const changes = {};
    keys.forEach((k) => {
      const b = before[k] ?? null;
      const a = after[k] ?? null;
      if ((b ?? "") !== (a ?? "")) changes[k] = { before: b, after: a };
    });
    return changes;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const now = serverTimestamp();
    const currentUser = auth.currentUser;

    const base = {
      ad: nameCaseTR(formData.ad || ""),
      soyad: upperTR(formData.soyad || ""),
      gorev: upperTR(formData.gorev || ""),
      not: (formData.not || "").trim(),
      tesis: upperTR(formData.tesis || ""),
      tecrube: digitsOnly(formData.tecrube || ""),
      telefon: digitsOnly(formData.telefon || ""),
      adres: (formData.adres || "").trim(),
      yakinAdSoyad: nameCaseTR(formData.yakinAdSoyad || ""),
      yakinTelefon: digitsOnly(formData.yakinTelefon || ""),
      yakinIliski: nameCaseTR(formData.yakinIliski || ""),
      iban: normalizeIBAN(formData.iban || ""),
      maas: digitsOnly(formData.maas || ""),
      sgkDurumu: (formData.sgkDurumu || "").trim(),
      dogumTarihi: (formData.dogumTarihi || ""),
      ogrenimDurumu: (formData.ogrenimDurumu || "").trim(),
    };

    const tc = (formData.tc || "").trim();
    if (!base.ad || !base.soyad || !tc || !base.gorev || !base.tesis || !base.tecrube) {
      return alert("Zorunlu alanlar: ad, soyad, tc, görev, tesis, tecrübe.");
    }
    if (!/^[0-9]{11}$/.test(tc)) return alert("TC kimlik numarası 11 haneli olmalı.");

    if (base.iban && !isTRIbanFormatLikely(base.iban)) {
      return alert("IBAN formatı hatalı (TR ile başlamalı, 24–26 karakter).");
    }

    if (formMode === "create") {
      if (!formData.girisTarihi) return alert("İlk giriş tarihi zorunludur.");
      const qTC = query(collection(db,"personeller"), where("tc","==",tc));
      const existing = await getDocs(qTC);

      if (!existing.empty) {
        const ref = existing.docs[0].ref;
        const existingData = existing.docs[0].data();
        const existingId = existing.docs[0].id;
        const tarih = { ...(existingData.tarih || {}) };

        if ((existingData.karaListe === true || voteCountFor(existingId) >= 3) && formData.girisTarihi) {
          return alert("Bu personel 3/3 kara listede. Giriş eklenemez.");
        }

        const aktif = isActiveByTarih(tarih);
        const yeniGiris = formData.girisTarihi ? fromDateInputToDot(formData.girisTarihi) : "";
        const yeniCikis = formData.cikisTarihi ? fromDateInputToDot(formData.cikisTarihi) : "";

        if (aktif) {
          if (!yeniCikis) return alert("Bu personel AKTİF. Çıkış tarihi gerekli.");
          const lastG = lastEntry(tarih, "giris");
          if (!lastG) return alert("Giriş kaydı yok.");
          if (toISO(yeniCikis) < toISO(lastG.value)) {
            return alert(`Çıkış tarihi son girişten önce olamaz. Son giriş: ${lastG.value}`);
          }
          const idx = 1 + Math.max(0, ...Object.keys(tarih).filter(k=>k.startsWith("cikis")).map(k=>parseInt(k.replace("cikis",""),10)||0));
          tarih[`cikis${idx}`] = yeniCikis;
        } else {
          if (!yeniGiris) return alert("Bu personel PASİF. Yeni giriş tarihi gerekli.");
          const idx = 1 + Math.max(0, ...Object.keys(tarih).filter(k=>k.startsWith("giris")).map(k=>parseInt(k.replace("giris",""),10)||0));
          tarih[`giris${idx}`] = yeniGiris;
        }

        const before = existingData || {};
        const after = { ...before, ...base, tarih, updateBy: currentUser.email, updateAt: now };
        const histKeys = ["ad","soyad","gorev","not","tesis","tecrube","telefon","adres","yakinAdSoyad","yakinTelefon","yakinIliski","iban","maas","tarih","sgkDurumu","dogumTarihi","ogrenimDurumu"];
        const changes = diffObjects(before, after, histKeys);

        await updateDoc(ref, after);
        await addDoc(collection(db,"personelHistory"), {
          personelId: existingId, action:"update_from_create_flow", changes, by: currentUser.email, at: serverTimestamp(),
        });
        alert("Kayıt güncellendi.");
      } else {
        const tarih = { giris1: fromDateInputToDot(formData.girisTarihi) };
        if (formData.cikisTarihi) {
          const dash = fromDateInputToDot(formData.cikisTarihi);
          if (toISO(dash) < toISO(formatDateDot(tarih.giris1))) {
            return alert(`İlk çıkış tarihi, ilk girişten önce olamaz. İlk giriş: ${formatDateDot(tarih.giris1)}`);
          }
          tarih.cikis1 = dash;
        }
        const res = await addDoc(collection(db,"personeller"), {
          ...base, tc, tarih, karaListe:false, createdBy: currentUser.email, createdAt: now,
        });
        await addDoc(collection(db,"personelHistory"), {
          personelId: res.id, action:"create",
          changes: Object.fromEntries(Object.keys({ ...base, tc, tarih, karaListe:false }).map(k=>[k,{before:null, after:({ ...base, tc, tarih, karaListe:false }[k])}])),
          by: currentUser.email, at: serverTimestamp(),
        });
        alert("Yeni personel eklendi.");
      }
    } else {
      if (!editTarget) return;
      const snap = await getDocs(query(collection(db,"personeller"), where("tc","==",editTarget.tc)));
      if (snap.empty) return alert("Kayıt bulunamadı.");
      const ref = snap.docs[0].ref;
      const existing = snap.docs[0].data();
      const tarih = { ...(existing.tarih || {}) };

      const yeniGiris = formData.girisTarihi ? fromDateInputToDot(formData.girisTarihi) : "";
      const yeniCikis = formData.cikisTarihi ? fromDateInputToDot(formData.cikisTarihi) : "";

      // ✅ DÜZENLE: Artık "yeni giriş/çıkış ekleme" değil, son GİRİŞ / son ÇIKIŞ tarihini GÜNCELLEME mantığı
      // (Mevcut kuralları bozmadan mantıksız tarihleri engeller)
      const getSortedEntries = (t, prefix) => {
        return Object.entries(t || {})
          .filter(([k, v]) => k.startsWith(prefix) && v)
          .map(([k, v]) => ({
            key: k,
            idx: parseInt(String(k).replace(prefix, ""), 10) || 0,
            value: formatDateDot(v),
          }))
          .sort((a, b) => a.idx - b.idx);
      };

      const girisArr = getSortedEntries(tarih, "giris");
      const cikisArr = getSortedEntries(tarih, "cikis");
      const lastG = girisArr.length ? girisArr[girisArr.length - 1] : null;
      const lastC = cikisArr.length ? cikisArr[cikisArr.length - 1] : null;
      const aktif = isActiveByTarih(tarih);
      const originalGiris = formSnapshot?.girisTarihi ? fromDateInputToDot(formSnapshot.girisTarihi) : "";
      const originalCikis = formSnapshot?.cikisTarihi ? fromDateInputToDot(formSnapshot.cikisTarihi) : "";
      const girisChanged = !!yeniGiris && yeniGiris !== originalGiris;
      const cikisChanged = !!yeniCikis && yeniCikis !== originalCikis;
      const nextIndexFor = (prefix) => 1 + Math.max(
        0,
        ...Object.keys(tarih)
          .filter((k) => k.startsWith(prefix))
          .map((k) => parseInt(String(k).replace(prefix, ""), 10) || 0)
      );

      if ((existing?.karaListe === true || existing?.karaListe?.durum === true) && yeniGiris) {
        // Kara listeli personelde giriş tarihi değiştirme = fiilen "giriş işlemi" gibi değerlendirilebilir
        // (Mevcut kuralı bozmamak için engelliyoruz)
        return alert("Bu personel kara listede. Giriş tarihi değiştirilemez.");
      }

      // Son giriş/çıkış güncelleme kuralları
      let reentryHandled = false;
      if (!aktif && lastC && girisChanged && toISO(yeniGiris) > toISO(lastC.value)) {
        const nextGirisIdx = nextIndexFor("giris");
        tarih[`giris${nextGirisIdx}`] = yeniGiris;

        if (cikisChanged && yeniCikis) {
          if (toISO(yeniCikis) < toISO(yeniGiris)) {
            return alert(`Çıkış tarihi yeni girişten önce olamaz. Yeni giriş: ${yeniGiris}`);
          }
          tarih[`cikis${nextGirisIdx}`] = yeniCikis;
        }

        reentryHandled = true;
      }

      if (!reentryHandled && lastG) {
        // 1) GİRİŞ güncelle
        if (yeniGiris) {
          // Önceki çıkış (varsa) yeni girişten sonra/aynı olmalı
          const prevExitRaw = tarih[`cikis${Math.max(0, lastG.idx - 1)}`];
          const prevExit = prevExitRaw ? formatDateDot(prevExitRaw) : "";
          if (prevExit && toISO(yeniGiris) < toISO(prevExit)) {
            return alert(`Giriş tarihi bir önceki çıkıştan önce olamaz. Önceki çıkış: ${prevExit}`);
          }

          // Aynı periyodun çıkışı (varsa) yeni girişten önce olamaz
          const matchExitRaw = tarih[`cikis${lastG.idx}`];
          const matchExit = matchExitRaw ? formatDateDot(matchExitRaw) : "";
          if (matchExit && toISO(yeniGiris) > toISO(matchExit)) {
            return alert(`Giriş tarihi ilgili çıkıştan sonra olamaz. Çıkış: ${matchExit}`);
          }

          tarih[lastG.key] = yeniGiris;
        }

        // 2) ÇIKIŞ güncelle / ekle (aktif personelde son çıkışı yoksa oluşturur)
        if (yeniCikis) {
          const girisVal = yeniGiris ? yeniGiris : formatDateDot(tarih[lastG.key]);
          if (toISO(yeniCikis) < toISO(girisVal)) {
            return alert(`Çıkış tarihi son girişten önce olamaz. Son giriş: ${girisVal}`);
          }
          // Çıkışı her zaman son girişin index'i ile eşleştiriyoruz (cikis{girisIdx})
          tarih[`cikis${lastG.idx}`] = yeniCikis;
        }
      } else if (!reentryHandled) {
        // Hiç giriş yoksa: giriş zorunlu (editte bile mantıksız veri)
        if (yeniCikis) return alert("Giriş kaydı yok. Önce giriş tarihi giriniz.");
        if (yeniGiris) {
          tarih["giris1"] = yeniGiris;
        }
      }

      const before = existing || {};
      const after = { ...before, ...base, tarih, updateBy: auth.currentUser.email, updateAt: serverTimestamp() };
      const histKeys = ["ad","soyad","gorev","not","tesis","tecrube","telefon","adres","yakinAdSoyad","yakinTelefon","yakinIliski","iban","maas","tarih","sgkDurumu","dogumTarihi","ogrenimDurumu"];
      const changes = diffObjects(before, after, histKeys);

      await updateDoc(ref, after);
      await addDoc(collection(db,"personelHistory"), {
        personelId: snap.docs[0].id, action:"update", changes, by: auth.currentUser.email, at: serverTimestamp(),
      });
      alert("Kayıt güncellendi.");
    }

    setFormMode("create");
    setEditTarget(null);
    setFormSnapshot(null);
    setExtraOpen(false);
    setFormData({
      ad:"", soyad:"", tc:"", gorev:"", not:"", tesis:"", tecrube:"",
      girisTarihi:"", cikisTarihi:"",
      telefon:"", adres:"", yakinAdSoyad:"", yakinTelefon:"", yakinIliski:"",
      iban:"",
      maas:"",
      sgkDurumu:"", dogumTarihi:"", ogrenimDurumu:""
    });
    await fetchPersoneller();
  };

  const startEdit = (p) => {
    setFormMode("edit"); setEditTarget(p);
    const base = {
      ad: p.ad || "",
      soyad: upperTR(p.soyad || ""),
      tc: p.tc || "",
      gorev: upperTR(p.gorev || ""),
      not: p.not || "",
      tesis: upperTR(p.tesis || ""),
      tecrube: digitsOnly(p.tecrube || ""),
      girisTarihi:"", cikisTarihi:"",
      telefon: digitsOnly(p.telefon || ""),
      adres: p.adres || "",
      yakinAdSoyad: p.yakinAdSoyad || "",
      yakinTelefon: digitsOnly(p.yakinTelefon || ""),
      yakinIliski: p.yakinIliski || "",
      iban: normalizeIBAN(p.iban || ""),
       maas: digitsOnly(p.maas || ""),
      sgkDurumu: p.sgkDurumu || "",
      dogumTarihi: p.dogumTarihi || "",
      ogrenimDurumu: p.ogrenimDurumu || "",
    };
    // ✅ Düzenle modunda: son GİRİŞ / son ÇIKIŞ tarihlerini form'a taşı (input type=date için YYYY-MM-DD)
    try {
      const tarihMap = (p && p.tarih && typeof p.tarih === "object") ? p.tarih : {};
      const lg = lastEntry(tarihMap, "giris");
      const lc = lastEntry(tarihMap, "cikis");
      if (lg?.value) base.girisTarihi = toISO(lg.value);
      if (lc?.value) base.cikisTarihi = toISO(lc.value);
    } catch (e) {}
    setFormData(base); setFormSnapshot(base);
    setExtraOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => {
    setFormMode("create"); setEditTarget(null); setFormSnapshot(null);
    setExtraOpen(false);
    setFormData({
      ad:"", soyad:"", tc:"", gorev:"", not:"", tesis:"", tecrube:"",
      girisTarihi:"", cikisTarihi:"",
      telefon:"", adres:"", yakinAdSoyad:"", yakinTelefon:"", yakinIliski:"",
      iban:"",
      maas:"",
      sgkDurumu:"", dogumTarihi:"", ogrenimDurumu:""
    });
  };
  const formDirty = formSnapshot && JSON.stringify(formData) !== JSON.stringify(formSnapshot);

  const [tesisDdOpen, setTesisDdOpen] = useState(false);
  const tesisDdRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (!tesisDdRef.current) return;
      if (!tesisDdRef.current.contains(e.target)) setTesisDdOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const dynamicTesisOptions = useMemo(() => {
    const f = { ...filters, tesisList: [] };
    const pass = (p) => {
      const okAd = !f.ad || (p.ad || "").toLowerCase().includes(f.ad.toLowerCase());
      const okSoy = !f.soyad || (p.soyad || "").toLowerCase().includes(f.soyad.toLowerCase());
      const okTc = !f.tc || (p.tc || "").includes(f.tc);
      const okGorev = !f.gorev || (p.gorev || "").toLowerCase().includes(f.gorev.toLowerCase());
      const okTecrube = !f.tecrube || (p.tecrube || "").toLowerCase().includes(f.tecrube.toLowerCase());
      const aktif = isActiveByTarih(p.tarih || {});
      const okDurum = !f.durum || (f.durum === "Aktif" && aktif) || (f.durum === "Pasif" && !aktif);
// Kara liste UI'da tamamen devre dışı (sayfada görünmeyecek)
const okKara = true;
      const lastG = lastEntry(p.tarih || {}, "giris");
      const lastC = lastExit(p.tarih || {});
      const lastGISO = lastG ? toISO(lastG.value) : "";
      const lastCISO = lastC ? toISO(lastC.value) : "";
      const gFromOk = !f.girisFrom || (lastGISO && lastGISO >= f.girisFrom);
      const gToOk   = !f.girisTo   || (lastGISO && lastGISO <= f.girisTo);
      const cFromOk = !f.cikisFrom || (lastCISO && lastCISO >= f.cikisFrom);
      const cToOk   = !f.cikisTo   || (lastCISO && lastCISO <= f.cikisTo);
      return okAd && okSoy && okTc && okGorev && okTecrube && okDurum && okKara && gFromOk && gToOk && cFromOk && cToOk;
    };
    const set = new Set();
    personeller.forEach(p => { if (pass(p) && p.tesis) set.add(p.tesis); });
    return Array.from(set).sort(byLocale);
  }, [personeller, filters, votesMap]);

  useEffect(() => {
    if (!filters.tesisList?.length) return;
    const keep = new Set(dynamicTesisOptions);
    const next = filters.tesisList.filter(t => keep.has(t));
    if (next.length !== filters.tesisList.length) {
      setFilters(f => ({ ...f, tesisList: next }));
    }
    // eslint-disable-next-line
  }, [dynamicTesisOptions]);

  const toggleAllTesis = (on) => setFilters(f=>({ ...f, tesisList: on ? [...dynamicTesisOptions] : [] }));
  const toggleOneTesis = (name) => {
    setFilters(f => {
      const set = new Set(f.tesisList);
      if (set.has(name)) set.delete(name); else set.add(name);
      return { ...f, tesisList: Array.from(set).sort(byLocale) };
    });
  };
  const tesisButtonLabel = () => {
    if (filters.tesisList.length === 0) return "(Hepsi)";
    if (filters.tesisList.length === dynamicTesisOptions.length) return "(Hepsi)";
    if (filters.tesisList.length === 1) return filters.tesisList[0];
    return `${filters.tesisList.length} seçili`;
  };

  const filtered = useMemo(() => {
    const f = filters;
    let list = personeller.filter((p) => {
      const okAd = !f.ad || (p.ad || "").toLowerCase().includes(f.ad.toLowerCase());
      const okSoy = !f.soyad || (p.soyad || "").toLowerCase().includes(f.soyad.toLowerCase());
      const okTc = !f.tc || (p.tc || "").includes(f.tc);
      const okGorev = !f.gorev || (p.gorev || "").toLowerCase().includes(f.gorev.toLowerCase());
      const okTecrube = !f.tecrube || (p.tecrube || "").toLowerCase().includes(f.tecrube.toLowerCase());
      const okTesis = !f.tesisList?.length || (p.tesis && f.tesisList.includes(p.tesis));
      const aktif = isActiveByTarih(p.tarih || {});
      const okDurum = !f.durum || (f.durum === "Aktif" && aktif) || (f.durum === "Pasif" && !aktif);
 // Kara liste UI'da tamamen devre dışı (sayfada görünmeyecek)
const okKara = true;
      const lastG = lastEntry(p.tarih || {}, "giris");
      const lastC = lastExit(p.tarih || {});
      const lastGISO = lastG ? toISO(lastG.value) : "";
      const lastCISO = lastC ? toISO(lastC.value) : "";
      const gFromOk = !f.girisFrom || (lastGISO && lastGISO >= f.girisFrom);
      const gToOk   = !f.girisTo   || (lastGISO && lastGISO <= f.girisTo);
      const cFromOk = !f.cikisFrom || (lastCISO && lastCISO >= f.cikisFrom);
      const cToOk   = !f.cikisTo   || (lastCISO && lastCISO <= f.cikisTo);
      return okAd && okSoy && okTc && okGorev && okTecrube && okTesis && okDurum && okKara && gFromOk && gToOk && cFromOk && cToOk;
    });
    list.sort((a,b)=>
      (a.ad||"").localeCompare(b.ad||"","tr",{sensitivity:"base"}) ||
      (a.soyad||"").localeCompare(b.soyad||"","tr",{sensitivity:"base"}) ||
      (a.tesis||"").localeCompare(b.tesis||"","tr",{sensitivity:"base"}) ||
      (a.gorev||"").localeCompare(b.gorev||"","tr",{sensitivity:"base"})
    );
    return list;
  }, [personeller, filters]);

  useEffect(() => { setCurrentPage(1); }, [JSON.stringify(filters)]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const orderedColumns = useMemo(() => {
    const map = new Map(TABLE_COLUMNS.map((c) => [c.key, c]));
    const valid = columnLayout.filter((c) => map.has(c.key));
    const missing = TABLE_COLUMNS
      .filter((c) => !valid.some((v) => v.key === c.key))
      .map((c) => ({ key: c.key, visible: c.defaultVisible !== false }));
    return [...valid, ...missing].map((c) => ({ ...map.get(c.key), visible: c.visible !== false }));
  }, [columnLayout]);

  const visibleColumns = useMemo(() => orderedColumns.filter((c) => c.visible), [orderedColumns]);
  const hiddenColumns = useMemo(() => orderedColumns.filter((c) => !c.visible), [orderedColumns]);

  const moveColumn = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    setColumnLayout((cols) => {
      const next = [...cols];
      const from = next.findIndex((c) => c.key === fromKey);
      const to = next.findIndex((c) => c.key === toKey);
      if (from < 0 || to < 0) return cols;
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const setColumnVisible = (key, visible) => {
    setColumnLayout((cols) => cols.map((c) => c.key === key ? { ...c, visible } : c));
  };

  const showColumnAt = (key, toKey) => {
    setColumnLayout((cols) => {
      const next = cols.map((c) => c.key === key ? { ...c, visible: true } : c);
      if (!toKey) return next;
      const from = next.findIndex((c) => c.key === key);
      const to = next.findIndex((c) => c.key === toKey);
      if (from < 0 || to < 0 || from === to) return next;
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const saveColumnDesign = async () => {
    if (!me) return alert("Kullanıcı bilgisi bulunamadı.");
    try {
      const designId = `${DESIGN_MODULE}_${encodeURIComponent(me)}`;
      await setDoc(doc(db, "reportDesigns", designId), {
        module: DESIGN_MODULE,
        name: "Personel Listesi",
        ownerEmail: me,
        columns: columnLayout,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      alert("Tablo tasarımı kaydedildi.");
    } catch (err) {
      alert("Tasarım kaydedilemedi: " + err.message);
    }
  };

  const meVotedFor = (id) => votesMap.get(id)?.has(me) || false;
  const handleVote = async (personel) => {
    if (!canVote) return;
    if (meVotedFor(personel.id)) return alert("Bu personele zaten oy verdiniz.");
    if (isThreeOfThree(personel)) return alert("Zaten 3/3 kara listede.");
    await addDoc(collection(db,"blacklistVotes"), { personelId: personel.id, createdBy: me, createdAt: serverTimestamp() });
    setVotesMap((prev) => { const m = new Map(prev); if (!m.has(personel.id)) m.set(personel.id, new Set()); m.get(personel.id).add(me); return m; });
    const total = voteCountFor(personel.id) + 1;
    if (total >= 3 && !personel.karaListe) {
      await updateDoc(doc(db,"personeller", personel.id), { karaListe:true, updateBy: me, updateAt: serverTimestamp() });
      alert("3 oy tamamlandı: Personel kara listeye alındı.");
      await fetchPersoneller();
    } else {
      alert(`Oyunuz kaydedildi. Toplam oy: {total}/3`);
    }
  };

  const exportToExcel = () => {
    const dataToExport = filtered.map((p) => {
      const t = p.tarih || {};
      const girisler = Object.entries(t).filter(([k]) => k.startsWith("giris")).sort().map(([,v])=>formatDateDot(v)).join(", ");
      const cikislar = Object.entries(t).filter(([k]) => k.startsWith("cikis")).sort().map(([,v])=>formatDateDot(v)).join(", ");
      return {
        AD: nameCaseTR(p.ad || ""), SOYAD: upperTR(p.soyad || ""), TC: p.tc,
        GÖREV: upperTR(p.gorev || ""), TESİS: upperTR(p.tesis || ""), TECRÜBE_AY: (p.tecrube || ""),
        TELEFON: p.telefon || "", ADRES: p.adres || "", "YAKIN AD SOYAD": p.yakinAdSoyad || "",
        "YAKIN TELEFON": p.yakinTelefon || "", "YAKIN İLİŞKİ": p.yakinIliski || "", IBAN: p.iban || "",
         "MAAŞ": p.maas || "", 
        "GİRİŞ TARİHLERİ": girisler, "ÇIKIŞ TARİHLERİ": cikislar, "KARA LİSTE": p.karaListe ? "Evet":"Hayır",
        "SGK DURUMU": p.sgkDurumu || "",
        "DOĞUM TARİHİ": p.dogumTarihi || "",
        "ÖĞRENİM DURUMU": p.ogrenimDurumu || "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Personeller");
    XLSX.writeFile(wb, "personel_listesi.xlsx");
  };

  const renderHeaderCell = (key) => {
    if (["ad", "soyad", "tc", "gorev"].includes(key)) {
      const meta = TABLE_COLUMNS.find((c) => c.key === key);
      return (
        <div className="th-wrap">
          <div className="th-label">{meta?.label || key}</div>
          <input
            type="text"
            placeholder="filtrele"
            value={filters[key]}
            onChange={(e)=>setFilters(f=>({ ...f, [key]:e.target.value }))}
          />
        </div>
      );
    }

    const meta = TABLE_COLUMNS.find((c) => c.key === key);
    if (["telefon", "adres", "yakinAdSoyad", "yakinTelefon", "yakinIliski", "iban", "maas", "sgkDurumu", "dogumTarihi", "ogrenimDurumu"].includes(key)) {
      return (
        <div className="th-wrap">
          <div className="th-label">{meta?.label || key}</div>
          <div style={{ fontSize:11, color:"#64748b" }}>-</div>
        </div>
      );
    }

    if (key === "tesis") {
      return (
        <div className="th-wrap" ref={tesisDdRef}>
          <div className="th-label">TESİS</div>
          <button type="button" className="tesis-btn" onClick={()=>setTesisDdOpen(s=>!s)}>
            <span>{tesisButtonLabel()}</span>
            <span>▾</span>
          </button>

          {tesisDdOpen && (
            <div className="tesis-dd">
              <div className="head">
                <button type="button" onClick={()=>toggleAllTesis(true)} disabled={dynamicTesisOptions.length===0}>Hepsini Seç</button>
                <button type="button" onClick={()=>toggleAllTesis(false)}>Temizle</button>
              </div>
              <div className="list">
                {dynamicTesisOptions.map((t)=>(
                  <label key={t} className="item">
                    <input
                      type="checkbox"
                      checked={filters.tesisList.includes(t)}
                      onChange={()=>toggleOneTesis(t)}
                    />
                    <span>{t}</span>
                  </label>
                ))}
                {dynamicTesisOptions.length === 0 && (
                  <div style={{padding:"4px 2px", color:"#6b7280"}}>Uygun tesis yok</div>
                )}
              </div>
              <div className="head" style={{ justifyContent:"flex-end" }}>
                <button type="button" onClick={()=>setTesisDdOpen(false)}>Tamam</button>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (key === "tecrube") {
      return (
        <div className="th-wrap">
          <div className="th-label">TECRÜBE (ay)</div>
          <input
            type="text"
            placeholder="filtrele"
            value={filters.tecrube}
            onChange={(e)=>setFilters(f=>({ ...f, tecrube: e.target.value }))}
          />
        </div>
      );
    }

    if (key === "giris") {
      return (
        <div className="th-wrap">
          <div className="th-label">GİRİŞ TARİHLERİ</div>
          <div className="th-range">
            <input type="date" value={filters.girisFrom}
              onChange={(e)=>setFilters(f=>({ ...f, girisFrom: e.target.value }))} title="Başlangıç (son giriş ≥)"/>
            <input type="date" value={filters.girisTo}
              onChange={(e)=>setFilters(f=>({ ...f, girisTo: e.target.value }))} title="Bitiş (son giriş ≤)"/>
          </div>
        </div>
      );
    }

    if (key === "cikis") {
      return (
        <div className="th-wrap">
          <div className="th-label">ÇIKIŞ TARİHLERİ</div>
          <div className="th-range">
            <input type="date" value={filters.cikisFrom}
              onChange={(e)=>setFilters(f=>({ ...f, cikisFrom:e.target.value }))} title="Başlangıç (son çıkış ≥)"/>
            <input type="date" value={filters.cikisTo}
              onChange={(e)=>setFilters(f=>({ ...f, cikisTo: e.target.value }))} title="Bitiş (son çıkış ≤)"/>
          </div>
        </div>
      );
    }

    if (key === "durum") {
      return (
        <div className="th-wrap">
          <div className="th-label">DURUM</div>
          <select value={filters.durum} onChange={(e)=>setFilters(f=>({ ...f, durum: e.target.value }))}>
            <option value="">Hepsi</option>
            <option value="Aktif">Aktif</option>
            <option value="Pasif">Pasif</option>
          </select>
        </div>
      );
    }

    return null;
  };

  const renderBodyCell = (p, key, context) => {
    if (["ad", "soyad", "tc", "gorev", "tesis", "tecrube"].includes(key)) {
      return (
        <input
          value={
            editedData[p.id]?.[key] ??
            (key==="ad"     ? (p[key]||"") :
             key==="soyad"  ? upperTR(p[key]||"") :
             key==="gorev"  ? upperTR(p[key]||"") :
             key==="tesis"  ? upperTR(p[key]||"") :
             key==="tecrube"? digitsOnly(p[key]||"") : (p[key]||""))
          }
          onChange={(e)=>{
            if(key==="tc") return;
            const raw = e.target.value;
            const norm =
              key==="ad"     ? raw :
              key==="soyad"  ? upperTR(raw) :
              key==="gorev"  ? upperTR(raw) :
              key==="tesis"  ? upperTR(raw) :
              key==="tecrube"? digitsOnly(raw) : raw;
            setEditedData(prev=>({ ...prev, [p.id]:{ ...prev[p.id], [key]: norm }}));
          }}
        />
      );
    }

    if (key === "giris") {
      return context.girisler.map(([k, v]) => <div key={k} className="date-chip">{formatDateDot(v)}</div>);
    }
    if (key === "cikis") {
      return context.cikislar.map(([k, v]) => <div key={k} className="date-chip">{formatDateDot(v)}</div>);
    }
    if (key === "durum") {
      return <span className={`status ${context.aktif ? "on" : "off"}`}>{context.aktif ? "Aktif" : "Pasif"}</span>;
    }
    if (key === "telefon") return p.telefon || "";
    if (key === "adres") return p.adres || "";
    if (key === "yakinAdSoyad") return p.yakinAdSoyad || "";
    if (key === "yakinTelefon") return p.yakinTelefon || "";
    if (key === "yakinIliski") return p.yakinIliski || "";
    if (key === "iban") return p.iban || "";
    if (key === "maas") return p.maas || "";
    if (key === "sgkDurumu") return p.sgkDurumu || "";
    if (key === "dogumTarihi") return p.dogumTarihi || "";
    if (key === "ogrenimDurumu") return p.ogrenimDurumu || "";
    return null;
  };

  const [sumOpen, setSumOpen] = useState(true);
  const [tesisOpen, setTesisOpen] = useState({});
  const [gorevOpen, setGorevOpen] = useState({});
  const summary = useMemo(() => {
    const base = { total: filtered.length, byTesis:{}, byGorev:{}, byTesisGorev:{}, namesByTesisGorev:{} };
    filtered.forEach((p) => {
      const t = (p.tesis || "(Tesis Yok)").trim();
      const g = (p.gorev || "(Görev Yok)").trim();
      const nm = `${nameCaseTR(p.ad || "")} ${upperTR(p.soyad || "")}`.trim();
      base.byTesis[t] = (base.byTesis[t] || 0) + 1;
      base.byGorev[g] = (base.byGorev[g] || 0) + 1;
      if (!base.byTesisGorev[t]) base.byTesisGorev[t] = {};
      base.byTesisGorev[t][g] = (base.byTesisGorev[t][g] || 0) + 1;
      if (!base.namesByTesisGorev[t]) base.namesByTesisGorev[t] = {};
      if (!base.namesByTesisGorev[t][g]) base.namesByTesisGorev[t][g] = [];
      if (nm) base.namesByTesisGorev[t][g].push(nm);
    });
    Object.keys(base.namesByTesisGorev).forEach((t) => {
      Object.keys(base.namesByTesisGorev[t]).forEach((g) => base.namesByTesisGorev[t][g].sort(byLocale));
    });
    return base;
  }, [filtered]);

  return (
    <div className="list-container">
      <style>{`/* ========= GENEL ========= */
.list-container{ position:relative; padding:12px; font-family:system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial; overflow:visible !important; }

.form-grid{ display:grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap:10px; align-items:end; max-width:1100px; }
.form-grid input, .form-grid textarea, .form-grid select { padding:8px 10px; border:1px solid #d1d5db; border-radius:8px; font-size:13px; background:#fff; }
.form-actions{ display:flex; gap:8px; align-items:center; }
.btn{ padding:9px 12px; border-radius:8px; border:0; background:#111827; color:#fff; font-weight:600; cursor:pointer; }
.btn.muted{ background:#9ca3af; }
.btn.ghost{ background:#fff; color:#111827; border:1px solid #d1d5db; }

/* ========= TABLO SARICI ========= */
.table-scroller{
  position:relative; width:100%;
  border:1px solid #e5e7eb; border-radius:10px; background:#fff; z-index:0;
  overflow-x:auto !important; overflow-y:visible !important; -webkit-overflow-scrolling:touch;
}

/* ========= TABLO ========= */
table.narrow-table{
  width:100%; min-width:1280px;
  table-layout:auto;
  border-collapse:collapse; font-size:12px;
}
.narrow-table thead, .narrow-table thead th, .th-wrap{ overflow:visible !important; }
.narrow-table thead th{ background:#f8fafc; }
.narrow-table th,.narrow-table td{
  border:1px solid #e5e7eb; padding:6px; vertical-align:top;
  background:#fff; text-align:left; word-break:normal; overflow-wrap:anywhere; white-space:normal;
  position:static !important;
}
.th-wrap{ position:relative; display:flex; flex-direction:column; gap:4px; min-height:72px; }
.th-label{ font-weight:700; font-size:12px; text-align:left; }
.narrow-table thead th input,.narrow-table thead th select{ width:100%; padding:6px 8px; border:1px solid #d1d5db; border-radius:8px; font-size:12px; box-sizing:border-box; text-align:left; }

/* ========= SÜTUN MİN-WIDTH ========= */
.col-ad{ min-width:160px; width:auto !important; z-index:2; }
.col-soyad{ min-width:150px; width:auto !important; }
.col-tc{ min-width:130px; width:auto !important; }
.col-gorev{ min-width:160px; width:auto !important; }
.col-tesis{ min-width:160px; width:auto !important; }
.col-tecrube{ min-width:120px; width:auto !important; }
.col-giris{ min-width:180px; width:auto !important; }
.col-cikis{ min-width:180px; width:auto !important; }
.col-durum{ min-width:110px; width:auto !important; }
.col-islem{ min-width:120px; width:auto !important; }


/* ========= HÜCRE İÇİ ========= */
td input{ width:100%; padding:4px; border:1px solid #e5e7eb; border-radius:6px; font-size:12px; box-sizing:border-box; background:#fff; text-align:left; }
.date-chip{ background:#f1f5f9; border:1px solid #e2e8f0; padding:2px 6px; border-radius:6px; display:inline-block; margin:2px 4px 2px 0; line-height:1.2; }
.status.on{ background:#ecfdf5; color:#065f46; padding:2px 6px; border-radius:6px; font-weight:600; }
.status.off{ background:#f3f4f6; color:#374151; padding:2px 6px; border-radius:6px; font-weight:600; }
.actions{ white-space:nowrap; }
.actions button{ padding:6px 10px; border:1px solid #d1d5db; border-radius:8px; background:#fff; cursor:pointer; }

.toolbar{ display:flex; gap:10px; align-items:center; margin:12px 0; }
.toolbar button{ padding:6px 10px; border:1px solid #d1d5db; border-radius:8px; background:#fff; cursor:pointer; }

.pager{ margin-top:8px; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.pager button{ padding:4px 8px; border:1px solid #d1d5db; border-radius:8px; background:#fff; cursor:pointer; }
.pager .active{ background:#111827; color:#fff; }
.count{ margin-left:6px; }

.toggle{ width:22px; height:22px; display:inline-flex; align-items:center; justify-content:center; border:1px solid #64748b; border-radius:50%; background:#fff; cursor:pointer; font-weight:700; line-height:1; }
.toggle.toggle--open{ background:#eef2ff; }
.dashboard-summary{ background:#f5f5f5; border-radius:8px; padding:15px; }
.summary-list{ list-style:none; padding-left:0; }
.summary-sublist{ list-style:none; padding-left:28px; margin-top:6px; }
.summary-names{ list-style:disc; padding-left:24px; margin-top:4px; color:#334155; }

.iban-hint{ font-size:11px; color:#64748b; margin-left:6px; }
.badge{ display:inline-block; padding:2px 6px; border-radius:999px; font-size:11px; border:1px solid #e5e7eb; background:#f8fafc; margin-left:6px; }
.badge.warn{ background:#fff7ed; border-color:#fdba74; }
.badge.ok{ background:#ecfdf5; border-color:#6ee7b7; }

/* ========= TESİS DROPDOWN ========= */
.tesis-btn{ display:flex; align-items:center; justify-content:space-between; gap:8px; padding:6px 8px; border:1px solid #d1d5db; border-radius:8px; background:#fff; cursor:pointer; text-align:left; }
.tesis-btn *{ text-align:left !important; }

body, #root, main, .app, .content, .list-container, .table-scroller, .narrow-table, .narrow-table thead, .narrow-table thead th, .th-wrap{
  overflow:visible !important;
}

.tesis-dd{
  position:absolute !important;
  top:calc(100% + 6px) !important;
  left:0 !important;
  z-index:2147483647 !important;

  width:340px !important;
  max-width:min(90vw, 420px) !important;

  max-height:calc(100vh - 160px) !important;
  display:flex !important;
  flex-direction:column !important;
  overflow:hidden !important;

  background:#fff !important;
  color:#111827 !important;
  border:1px solid #e5e7eb !important;
  border-radius:10px !important;
  box-shadow:0 10px 30px rgba(0,0,0,.08) !important;
  text-align:left !important;
}
.tesis-dd .head{ display:flex !important; align-items:center !important; justify-content:space-between !important; gap:8px !important; padding:4px 2px !important; }
.tesis-dd .head button{ border:0 !important; background:#f1f5f9 !important; padding:6px 8px !important; border-radius:8px !important; cursor:pointer !important; }
.tesis-dd .list{
  flex:1 1 auto !important;
  max-height:inherit !important;
  overflow-y:auto !important;
  overflow-x:hidden !important;
  margin-top:6px !important;
  padding:0 4px !important;
}
.tesis-dd .item{
  display:grid !important;
  grid-template-columns:auto 1fr !important;
  align-items:center !important;
  gap:8px !important;
  padding:6px 0 !important;
  width:100% !important;
  direction:ltr !important;
}
.tesis-dd .item input{
  margin:0 !important;
  width:16px !important;
  height:16px !important;
  flex:0 0 auto !important;
}
.tesis-dd .item span{
  display:block !important;
  min-width:0 !important;
  max-width:100% !important;

  margin:0 !important; padding:0 !important;
  font-size:13px !important; line-height:1.35 !important; font-weight:600 !important;

  color:#111827 !important;
  text-align:left !important;
  white-space:normal !important; overflow-wrap:anywhere !important; word-break:break-word !important;

  opacity:1 !important; visibility:visible !important;
  text-indent:0 !important; transform:none !important; filter:none !important; float:none !important;
}
.tesis-dd .item:hover{ background:#f8fafc !important; }

/* AD sütunu görünür ve geniş */
.narrow-table th.col-ad,
.narrow-table td.col-ad{
  display: table-cell !important;
  visibility: visible !important;
  opacity: 1 !important;
  color: inherit !important;

  min-width: 200px !important;
  width: 16% !important;
  max-width: none !important;

  overflow: visible !important;
  white-space: normal !important;
  position: static !important;
}
.narrow-table th:first-child,
.narrow-table td:first-child{
  display: table-cell !important;
  visibility: visible !important;
  min-width: 200px !important;
  width: 16% !important;
  position: static !important;
  overflow: visible !important;
}
.narrow-table td.col-ad input{
  min-width: 180px !important;
  width: 100% !important;
  box-sizing: border-box !important;
}
table.narrow-table{
  table-layout: fixed !important;
}
 `}</style>

      {/* ❌ Formen formu görmesin */}
      {canUseForm && (
        <>
          <h2>Personel Ekle</h2>
          <form onSubmit={handleSubmit} className="form-grid">
            <input
              name="ad"
              placeholder="ad"
              value={formData.ad}
              onChange={(e)=>setFormData({...formData, ad: e.target.value})}
            />
            <input
              name="soyad"
              placeholder="soyad"
              value={formData.soyad}
              onChange={(e)=>setFormData({...formData, soyad: upperTR(e.target.value)})}
            />
            <input
              name="tc"
              placeholder="tc (11 hane)"
              value={formData.tc}
              onChange={(e)=>setFormData({...formData, tc: digitsOnly(e.target.value)})}
              required={formMode==="create"}
              disabled={formMode==="edit"}
            />
            <input
              name="gorev"
              placeholder="görev"
              value={formData.gorev}
              onChange={(e)=>setFormData({...formData, gorev: upperTR(e.target.value)})}
            />
            <input
              name="tesis"
              placeholder="tesis"
              value={formData.tesis}
              onChange={(e)=>setFormData({...formData, tesis: upperTR(e.target.value)})}
            />
            <input
              name="tecrube"
              placeholder="tecrübe (ay) — sadece sayı"
              value={formData.tecrube}
              onChange={(e)=>setFormData({...formData, tecrube: digitsOnly(e.target.value)})}
            />
            <input
              name="not"
              placeholder="not"
              value={formData.not}
              onChange={(e)=>setFormData({...formData, not:e.target.value})}
            />

            <input
              type="date"
              value={formData.girisTarihi}
              onChange={(e)=>setFormData({...formData, girisTarihi:e.target.value})}
              title="Giriş"
            />
            <input
              type="date"
              value={formData.cikisTarihi}
              onChange={(e)=>setFormData({...formData, cikisTarihi: e.target.value})}
              title="Çıkış"
            />

            <select
              name="sgkDurumu"
              value={formData.sgkDurumu}
              onChange={(e)=>setFormData({...formData, sgkDurumu: e.target.value})}
              title="SGK durumu"
            >
              <option value="">SGK durumu (seçiniz)</option>
              <option value="4A">4A</option>
              <option value="4B">4B</option>
              <option value="4C">4C</option>
              <option value="Emekli">Emekli</option>
              <option value="Stajyer">Stajyer</option>
              <option value="Diğer">Diğer</option>
            </select>

            <input
              type="date"
              name="dogumTarihi"
              value={formData.dogumTarihi}
              onChange={(e)=>setFormData({...formData, dogumTarihi:e.target.value})}
              title="Doğum tarihi"
            />

            <select
              name="ogrenimDurumu"
              value={formData.ogrenimDurumu}
              onChange={(e)=>setFormData({...formData, ogrenimDurumu: e.target.value})}
              title="Öğrenim durumu"
            >
              <option value="">Öğrenim durumu (seçiniz)</option>
              <option value="İlköğretim">İlköğretim</option>
              <option value="Ortaöğretim">Ortaöğretim</option>
              <option value="Lise">Lise</option>
              <option value="Ön Lisans">Ön Lisans</option>
              <option value="Lisans">Lisans</option>
              <option value="Yüksek Lisans">Yüksek Lisans</option>
              <option value="Doktora">Doktora</option>
            </select>

            <div className="form-actions">
              {formMode==="edit" && <button type="button" className="btn muted" onClick={cancelEdit}>Vazgeç</button>}
              <button type="submit" className="btn" disabled={formMode==="edit" && !formDirty}>
                {formMode==="create" ? "Ekle" : "GÜNCELLE"}
              </button>
              <button type="button" className="btn ghost" onClick={()=>setExtraOpen(s=>!s)} title="İletişim & Yakın Bilgileri alanlarını aç/kapat">+</button>
            </div>

            {(formMode === "edit" || extraOpen) && (
              <>
                <h3 style={{gridColumn:"1 / -1", marginTop:10}}>İletişim & Yakın Bilgileri</h3>
                <input name="telefon" placeholder="telefon"
                  value={formData.telefon}
                  onChange={(e)=>setFormData({...formData, telefon: digitsOnly(e.target.value)})}/>
                <input name="yakinTelefon" placeholder="yakın telefon"
                  value={formData.yakinTelefon}
                  onChange={(e)=>setFormData({...formData, yakinTelefon: digitsOnly(e.target.value)})}/>
                <input name="yakinAdSoyad" placeholder="yakın ad soyad"
                  value={formData.yakinAdSoyad}
                  onChange={(e)=>setFormData({...formData, yakinAdSoyad: e.target.value})}/>
                <input name="yakinIliski" placeholder="yakın ile ilişki (ör. Anne, Eş...)"
                  value={formData.yakinIliski}
                  onChange={(e)=>setFormData({...formData, yakinIliski: e.target.value})}/>
                <textarea name="adres" placeholder="adres" rows={2}
                  style={{gridColumn:"1 / -1"}}
                  value={formData.adres}
                  onChange={(e)=>setFormData({...formData, adres: e.target.value})}/>

                <h3 style={{gridColumn:"1 / -1"}}>IBAN</h3>
                <div style={{display:"flex", gap:8, alignItems:"center", gridColumn:"1 / -1"}}>
                  <input
                    name="iban"
                    placeholder="TR••••••••••••••••••••••"
                    value={formData.iban}
                    onChange={(e)=>{
                      const v = normalizeIBAN(e.target.value);
                      setFormData({...formData, iban: v});
                    }}
                    style={{ flex: 1 }}
                  />
                  {formData.iban ? (
                    isTRIbanFormatLikely(formData.iban) ? (
                      isAkbankIban(formData.iban)
                        ? <span className="badge ok">Akbank</span>
                        : <span className="badge warn" title="Akbank değil">!</span>
                    ) : <span className="badge warn">Format?</span>
                  ) : <span className="iban-hint">TR ile başlar, 24–26 karakter</span>}
                </div>
                 <input
      name="maas"
      placeholder="Maaş (₺)"
      value={formData.maas}
      onChange={(e)=>setFormData({
        ...formData,
        maas: digitsOnly(e.target.value)
      })}
    />
              </>
            )}
          </form>
        </>
      )}

      {/* Araç çubuğu ve tablo: sadece Admin/İK/Saha Yön (Formen görmez) */}
      {canViewList ? (
        <>
          <h2 style={{ marginTop: 18 }}>Personel Listesi</h2>
          <div className="toolbar">
            <button onClick={exportToExcel}>Excel indir</button>
          </div>

          <div style={{ marginBottom: 12, padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
              <strong>Tablo Sütun Sırası</strong>
              <button type="button" onClick={saveColumnDesign} style={{ padding: "7px 12px", background: "#2563eb", color: "#fff", border: 0, borderRadius: 6, cursor: "pointer", fontWeight: 700 }}>
                Tasarımı Kaydet
              </button>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {visibleColumns.map((col) => (
                <span
                  key={col.key}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", col.key)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => showColumnAt(e.dataTransfer.getData("text/plain"), col.key)}
                  title="Sürükleyerek sıralayabilirsiniz"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 9px", border: "1px solid #cbd5e1", borderRadius: 8, background: "#fff", cursor: "grab", fontSize: 12 }}
                >
                  <span style={{ color: "#64748b" }}>↕</span>
                  <span>{col.label}</span>
                  <button
                    type="button"
                    onClick={() => setColumnVisible(col.key, false)}
                    title="Listeden gizle"
                    style={{ border: 0, background: "transparent", cursor: "pointer", color: "#64748b", padding: 0 }}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => setColumnVisible(e.dataTransfer.getData("text/plain"), false)}
              style={{ marginTop: 10, padding: 10, border: "1px dashed #cbd5e1", borderRadius: 8, background: "#fff" }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: "#475569" }}>Gizli sütunlar</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", minHeight: 28 }}>
                {hiddenColumns.map((col) => (
                  <span
                    key={col.key}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", col.key)}
                    onClick={() => setColumnVisible(col.key, true)}
                    title="Tabloya geri almak için tıklayın veya başlıkların üzerine sürükleyin"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 9px", border: "1px solid #cbd5e1", borderRadius: 8, background: "#f1f5f9", cursor: "grab", fontSize: 12 }}
                  >
                    <span>{col.label}</span>
                  </span>
                ))}
                {hiddenColumns.length === 0 && <span style={{ fontSize: 12, color: "#94a3b8" }}>Gizli sütun yok</span>}
              </div>
            </div>
          </div>

          <div className="table-scroller">
            <table className="narrow-table">
              <thead>
                <tr>
                  {visibleColumns.map((col) => (
                    <th
                      key={col.key}
                      className={col.className}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", col.key)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => showColumnAt(e.dataTransfer.getData("text/plain"), col.key)}
                      title="Başlığı sürükleyerek sütun yerini değiştirebilirsiniz"
                      style={{ cursor: "grab" }}
                    >
                      {renderHeaderCell(col.key)}
                    </th>
                  ))}
                  {false && [
                    ["ad","AD"],["soyad","SOYAD"],["tc","TC"],
                    ["gorev","GÖREV"]
                  ].map(([key,label])=>(
                    <th key={key} className={`col-${key}`}>
                      <div className="th-wrap">
                        <div className="th-label">{label}</div>
                        <input type="text" placeholder="filtrele"
                          value={filters[key]}
                          onChange={(e)=>setFilters(f=>({ ...f, [key]:e.target.value }))}/>
                      </div>
                    </th>
                  ))}

                  {/* TESİS: Dinamik çoklu seçim */}
                  {false && (<>
                  <th className="col-tesis">
                    <div className="th-wrap" ref={tesisDdRef}>
                      <div className="th-label">TESİS</div>
                      <button type="button" className="tesis-btn" onClick={()=>setTesisDdOpen(s=>!s)}>
                        <span>{tesisButtonLabel()}</span>
                        <span>▾</span>
                      </button>

                      {tesisDdOpen && (
                        <div className="tesis-dd">
                          <div className="head">
                            <button type="button" onClick={()=>toggleAllTesis(true)} disabled={dynamicTesisOptions.length===0}>Hepsini Seç</button>
                            <button type="button" onClick={()=>toggleAllTesis(false)}>Temizle</button>
                          </div>
                          <div className="list">
                            {dynamicTesisOptions.map((t)=>(
                              <label key={t} className="item">
                                <input
                                  type="checkbox"
                                  checked={filters.tesisList.includes(t)}
                                  onChange={()=>toggleOneTesis(t)}
                                />
                                <span>{t}</span>
                              </label>
                            ))}
                            {dynamicTesisOptions.length === 0 && (
                              <div style={{padding:"4px 2px", color:"#6b7280"}}>Uygun tesis yok</div>
                            )}
                          </div>
                          <div className="head" style={{ justifyContent:"flex-end" }}>
                            <button type="button" onClick={()=>setTesisDdOpen(false)}>Tamam</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </th>

                  {/* TECRÜBE */}
                  <th className="col-tecrube">
                    <div className="th-wrap">
                      <div className="th-label">TECRÜBE (ay)</div>
                      <input
                        type="text"
                        placeholder="filtrele"
                        value={filters.tecrube}
                        onChange={(e)=>setFilters(f=>({ ...f, tecrube: e.target.value }))}
                      />
                    </div>
                  </th>

                  {/* GİRİŞ TARİHLERİ */}
                  <th className="col-giris">
                    <div className="th-wrap">
                      <div className="th-label">GİRİŞ TARİHLERİ</div>
                      <div className="th-range">
                        <input type="date" value={filters.girisFrom}
                          onChange={(e)=>setFilters(f=>({ ...f, girisFrom: e.target.value }))} title="Başlangıç (son giriş ≥)"/>
                        <input type="date" value={filters.girisTo}
                          onChange={(e)=>setFilters(f=>({ ...f, girisTo: e.target.value }))} title="Bitiş (son giriş ≤)"/>
                      </div>
                    </div>
                  </th>

                  {/* ÇIKIŞ TARİHLERİ */}
                  <th className="col-cikis">
                    <div className="th-wrap">
                      <div className="th-label">ÇIKIŞ TARİHLERİ</div>
                      <div className="th-range">
                        <input type="date" value={filters.cikisFrom}
                          onChange={(e)=>setFilters(f=>({ ...f, cikisFrom:e.target.value }))} title="Başlangıç (son çıkış ≥)"/>
                        <input type="date" value={filters.cikisTo}
                          onChange={(e)=>setFilters(f=>({ ...f, cikisTo: e.target.value }))} title="Bitiş (son çıkış ≤)"/>
                      </div>
                    </div>
                  </th>

                  {/* DURUM */}
                  <th className="col-durum">
                    <div className="th-wrap">
                      <div className="th-label">DURUM</div>
                      <select value={filters.durum} onChange={(e)=>setFilters(f=>({ ...f, durum: e.target.value }))}>
                        <option value="">Hepsi</option>
                        <option value="Aktif">Aktif</option>
                        <option value="Pasif">Pasif</option>
                      </select>
                    </div>
                  </th>

                  {/* KARA LİSTE filtresi – yalnız oy verebilenler görür */}


                  </>)}
                  <th className="col-islem">
                    <div className="th-wrap">
                      <div className="th-label">İŞLEM</div>
                      <div style={{ fontSize:11, color:"#64748b" }}>—</div>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {paginated.map((p) => {
                  const t = p.tarih || {};
                  const girisler = Object.entries(t).filter(([k]) => k.startsWith("giris")).sort();
                  const cikislar = Object.entries(t).filter(([k]) => k.startsWith("cikis")).sort();
                  const aktif = isActiveByTarih(t);
                  const votes = voteCountFor(p.id);
                  const meVoted = votesMap.get(p.id)?.has(me) || false;

                  return (
                    <tr key={p.id}>
                      {visibleColumns.map((col) => (
                        <td key={col.key} className={col.className}>
                          {renderBodyCell(p, col.key, { girisler, cikislar, aktif })}
                        </td>
                      ))}
                      {false && ["ad","soyad","tc","gorev","tesis","tecrube"].map((col)=>(
                        <td key={col} className={`col-${col}`}>
                          <input
                            value={
                              editedData[p.id]?.[col] ??
                              (col==="ad"     ? (p[col]||"") :
                               col==="soyad"  ? upperTR(p[col]||"") :
                               col==="gorev"  ? upperTR(p[col]||"") :
                               col==="tesis"  ? upperTR(p[col]||"") :
                               col==="tecrube"? digitsOnly(p[col]||"") : (p[col]||""))
                            }
                            onChange={(e)=>{
                              if(col==="tc") return;
                              const raw = e.target.value;
                              const norm =
                                col==="ad"     ? raw :
                                col==="soyad"  ? upperTR(raw) :
                                col==="gorev"  ? upperTR(raw) :
                                col==="tesis"  ? upperTR(raw) :
                                col==="tecrube"? digitsOnly(raw) : raw;
                              setEditedData(prev=>({ ...prev, [p.id]:{ ...prev[p.id], [col]: norm }}));
                            }}
                          />
                        </td>
                      ))}

                      {false && (<>
                      <td className="col-giris">
                        {girisler.map(([k, v]) => <div key={k} className="date-chip">{formatDateDot(v)}</div>)}
                      </td>

                      <td className="col-cikis">
                        {cikislar.map(([k, v]) => <div key={k} className="date-chip">{formatDateDot(v)}</div>)}
                      </td>

                      <td className="col-durum">
                        <span className={`status ${aktif ? "on" : "off"}`}>{aktif ? "Aktif" : "Pasif"}</span>
                      </td>
                      </>)}


                      <td className="actions col-islem">
                        <button onClick={()=>startEdit(p)}>Düzenle</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Sayfalama */}
          <div className="pager">
            Sayfa:
            {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
              <button key={p} className={p === currentPage ? "active" : ""} onClick={()=>setCurrentPage(p)}>{p}</button>
            ))}
            <span className="count">Toplam: {filtered.length}</span>
          </div>

          {/* Satır içi güncelle */}
          <div style={{ marginTop: 8 }}>
            <button
              onClick={async () => {
                if (!isDirtyBulk) return;
                const now = serverTimestamp();
                const keys = ["ad","soyad","gorev","tesis","tecrube"];
                for (const id in editedData) {
                  const ref = doc(db, "personeller", id);
                  const current = personeller.find(x => x.id === id) || {};
                  const after = { ...current, ...editedData[id] };
                  const changes = diffObjects(current, after, keys);
                  await updateDoc(ref, { ...editedData[id], updateBy: auth.currentUser.email, updateAt: now });
                  if (Object.keys(changes).length) {
                    await addDoc(collection(db, "personelHistory"), {
                      personelId: id,
                      action: "inline_update",
                      changes,
                      by: auth.currentUser.email,
                      at: serverTimestamp(),
                    });
                  }
                }
                alert("Güncellemeler kaydedildi.");
                setEditedData({});
                await fetchPersoneller();
              }}
              className="btn"
              disabled={!isDirtyBulk}
              title={isDirtyBulk ? "Değişiklikleri kaydet" : "Değişiklik yok"}
            >
              Satır içi güncelle
            </button>
          </div>
        </>
      ) : (
        // Formen için uyarı göstermiyoruz; doğrudan özet görsün
        (!isForeman && (
          <div style={{ marginTop: 12, color: "#6b7280" }}>
            Bu sayfada personel listesine erişim yetkiniz yok. Aşağıda yetkili olduğunuz tesis/personellere göre özet bilgileri görebilirsiniz.
          </div>
        ))
      )}

      {/* Özet (herkese açık; veri yetkisi already allowedTesis ile kısıtlı) */}
      <div className="dashboard-summary" style={{ marginTop: 16 }}>
        <h3 style={{ display:"flex", alignItems:"center", gap:8 }}>
          <button className={`toggle ${sumOpen ? "toggle--open":""}`} onClick={()=>setSumOpen(s=>!s)} title={sumOpen ? "Kapat":"Aç"}>{sumOpen ? "−":"+"}</button>
          <span>Toplam Personel Sayısı: {summary.total}</span>
        </h3>

        {sumOpen && (
          <>
            <h4 style={{ marginTop:10 }}>Tesis Bazında Dağılım</h4>
            <ul className="summary-list">
              {Object.keys(summary.byTesis).sort(byLocale).map((tesis)=>(
                <li key={tesis} style={{ marginBottom:6 }}>
                  <button
                    className={`toggle ${tesisOpen[tesis] ? "toggle--open":""}`}
                    onClick={()=>setTesisOpen(s=>({ ...s, [tesis]: !s[tesis] }))}
                    title={tesisOpen[tesis] ? "Kapat":"Aç"} style={{ marginRight:8 }}
                  >
                    {tesisOpen[tesis] ? "−":"+"}
                  </button>
                  <strong>{tesis.toUpperCase()}</strong>: {summary.byTesis[tesis]}
                  {tesisOpen[tesis] && (
                    <ul className="summary-sublist">
                      {Object.entries(summary.byTesisGorev[tesis] || {}).sort(([a],[b])=>byLocale(a,b)).map(([g,n])=>{
                        const key = `${tesis}||${g}`; const open = gorevOpen[key] ?? false;
                        return (
                          <li key={key} style={{ marginBottom:4 }}>
                            <button
                              className={`toggle ${open ? "toggle--open":""}`}
                              onClick={()=>setGorevOpen(s=>({ ...s, [key]: !s[key] }))}
                              title={open ? "Kapat":"Aç"} style={{ marginRight:8 }}
                            >
                              {open ? "−":"+"}
                            </button>
                            {g.toUpperCase()}: {n}
                            {open && (
                              <ul className="summary-names">
                                {(summary.namesByTesisGorev[tesis]?.[g] || []).map((nm)=>(<li key={nm}>{nm}</li>))}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              ))}
            </ul>

            <h4>Görev Bazında Dağılım</h4>
            <ul className="summary-list">
              {Object.entries(summary.byGorev).sort(([a],[b])=>byLocale(a,b)).map(([g,n])=>(
                <li key={g}>{g.toUpperCase()}: {n}</li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default PersonelListesi;
